import { useCallback, useEffect, useMemo, useState } from "react";
import { captureError } from "@/lib/sentry";
import { Link } from "react-router-dom";
import AppLayout from "@/components/AppLayout";
import GameCard from "@/components/GameCard";
import { HotTakeCard } from "@/pages/HotTakes";
import { api } from "@/lib/api";
import { AI_MODES, CONTENT_STYLES } from "@/lib/constants";
import { useAuth } from "@/context/AuthContext";
import { ChartLineUpIcon, FireIcon, TrophyIcon, SparkleIcon, ArrowRightIcon, MedalIcon, BellRingingIcon } from "@phosphor-icons/react";

export default function Dashboard() {
  const { user } = useAuth();
  const [rawGames, setRawGames] = useState([]);
  const [storylines, setStorylines] = useState([]);
  const [xp, setXp] = useState(null);
  const [hotTakes, setHotTakes] = useState([]);
  const [alerts, setAlerts] = useState([]);

  const loadData = useCallback(async () => {
    try {
      const [g, s, x, h, a] = await Promise.all([
        api.get("/games"),
        api.get("/storylines"),
        api.get("/xp/me"),
        api.get("/hot-takes/today"),
        api.get("/alerts"),
      ]);
      setRawGames(g.data || []);
      setStorylines((s.data || []).slice(0, 3));
      setXp(x.data);
      setHotTakes((h.data || []).slice(0, 2));
      setAlerts((a.data?.alerts || []).slice(0, 4));
    } catch (err) {
      captureError("dashboard load failed:", err);
    }
  }, []);

  const voteTake = useCallback(async (id, choice) => {
    try {
      const r = await api.post(`/hot-takes/${id}/vote`, { choice });
      setHotTakes((prev) => prev.map((t) => (t.id === id ? r.data : t)));
      const x = await api.get("/xp/me");
      setXp(x.data);
    } catch (err) {
      captureError("dashboard vote failed:", err);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Prioritize games for user's favorite teams/leagues (memoized)
  const games = useMemo(() => {
    const favTeams = new Set(user?.favorite_teams || []);
    const favLeagues = new Set(user?.favorite_leagues || []);
    return [...rawGames]
      .map((game) => ({
        game,
        score:
          (game.status === "LIVE" ? 100 : game.status === "UPCOMING" ? 50 : 10) +
          (favLeagues.has(game.league) ? 30 : 0) +
          (favTeams.has(`${game.league}:${game.home_team_abbr}`) ? 50 : 0) +
          (favTeams.has(`${game.league}:${game.away_team_abbr}`) ? 50 : 0),
      }))
      .sort((a, b) => b.score - a.score)
      .map((x) => x.game)
      .slice(0, 6);
  }, [rawGames, user?.favorite_teams, user?.favorite_leagues]);

  const liveCount = useMemo(() => games.filter((g) => g.status === "LIVE").length, [games]);
  const styleLabel = CONTENT_STYLES.find((s) => s.value === user?.content_style)?.label || "Casual Fan";

  return (
    <AppLayout>
      <div className="mb-6 fade-up flex items-end justify-between gap-3 flex-wrap" data-testid="dashboard-heading">
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#0A84FF] mb-2">Game Day, {user?.name?.split(" ")[0] || "Fan"}</p>
          <h1 className="font-display font-bold text-4xl md:text-5xl uppercase tracking-tighter">Your Command Center</h1>
        </div>
        <Link to="/onboarding" data-testid="edit-fan-dna" className="text-[11px] font-bold uppercase tracking-[0.2em] text-white/40 hover:text-white">
          Edit My Fan DNA →
        </Link>
      </div>

      {/* Fan DNA strip */}
      <div className="mb-6 flex flex-wrap items-center gap-2 text-[11px] font-bold uppercase tracking-wider" data-testid="fan-dna-strip">
        <span className="px-2.5 py-1 rounded bg-white/5 border border-white/10 text-white/70">
          Style · <span className="text-[#0A84FF]">{styleLabel}</span>
        </span>
        <span className="px-2.5 py-1 rounded bg-white/5 border border-white/10 text-white/70">
          Lang · <span className="text-[#32D74B]">{user?.language_preference?.toUpperCase() || "EN"}</span>
        </span>
        <span className="px-2.5 py-1 rounded bg-white/5 border border-white/10 text-white/70">
          Plan · <span className="text-[#FDB927]">{(user?.plan || "free").toUpperCase()}</span>
        </span>
        {(user?.favorite_leagues || []).map((lg) => (
          <span key={lg} className="px-2.5 py-1 rounded bg-white/5 border border-white/10 text-white/70">
            {lg}
          </span>
        ))}
      </div>

      {/* Fan XP banner */}
      {xp && (
        <div className="mb-6 p-4 rounded-md bg-[#121212] border border-white/10 flex items-center gap-4" data-testid="dashboard-xp-card">
          <div className="w-11 h-11 rounded-md flex items-center justify-center shrink-0" style={{ backgroundColor: `${xp.rank_color}1f`, border: `1px solid ${xp.rank_color}55` }}>
            <MedalIcon weight="fill" size={22} style={{ color: xp.rank_color }} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between mb-1.5">
              <p className="font-display font-bold text-base uppercase tracking-tight" style={{ color: xp.rank_color }} data-testid="dashboard-xp-rank">{xp.rank}</p>
              <p className="text-[11px] font-bold uppercase tracking-wider text-white/45">
                {xp.next_rank ? `${xp.xp_to_next} XP to ${xp.next_rank}` : "Max rank — Legend"}
              </p>
            </div>
            <div className="h-2 rounded-full bg-white/10 overflow-hidden">
              <div className="h-full rounded-full transition-all" style={{ width: `${xp.progress_pct}%`, backgroundColor: xp.rank_color }} />
            </div>
          </div>
          <span className="font-num text-2xl font-black shrink-0" data-testid="dashboard-xp-value">{xp.xp}<span className="text-xs text-white/40 ml-1">XP</span></span>
        </div>
      )}

      {/* KPI strip */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-8">
        <KPI icon={FireIcon} label="Live Now" value={liveCount} testId="kpi-live" />
        <KPI icon={TrophyIcon} label="Today's Games" value={games.length} testId="kpi-today" />
        <KPI icon={ChartLineUpIcon} label="Watching" value={(user?.favorite_players || []).length} testId="kpi-watching" />
        <KPI icon={SparkleIcon} label="Storylines" value={storylines.length} testId="kpi-stories" />
      </div>

      {/* AI Modes Lab */}
      <div className="mb-8 fade-up" data-testid="ai-modes-section">
        <SectionTitle title="AI Modes" link="/games" linkLabel="Apply to games →" />
        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-2">
          {AI_MODES.map((m) => (
            <Link
              key={m.key}
              to="/games"
              data-testid={`dash-mode-${m.key}`}
              className="p-3 rounded-md bg-[#121212] border border-white/10 hover:border-[#0A84FF]/50 hover:bg-[#0A84FF]/5 transition-colors group"
            >
              <m.icon weight="bold" size={18} className="text-[#0A84FF] mb-2 group-hover:scale-110 transition-transform" />
              <p className="font-display font-bold text-[11px] uppercase tracking-tight leading-tight">{m.label}</p>
            </Link>
          ))}
        </div>
      </div>

      {/* Hot Take Center preview */}
      {hotTakes.length > 0 && (
        <div className="mb-8 fade-up" data-testid="dashboard-hot-takes">
          <SectionTitle title="Hot Take Center" link="/hot-takes" linkLabel="All debates →" />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {hotTakes.map((t) => <HotTakeCard key={t.id} take={t} onVote={voteTake} compact />)}
          </div>
        </div>
      )}

      {/* Storyline Alerts — live-slate signals (nail-biters, momentum swings, finals) */}
      {alerts.length > 0 && (
        <div className="mb-8 fade-up" data-testid="dashboard-alerts">
          <SectionTitle title="Storyline Alerts" link="/games" linkLabel="See games →" />
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {alerts.map((a) => (
              <Link
                key={a.id}
                to={`/games/${a.game_id}`}
                data-testid={`dash-alert-${a.id}`}
                className="p-4 rounded-md bg-[#121212] border border-white/10 hover:border-white/25 transition-colors flex items-start gap-3"
              >
                <span className="mt-0.5 w-8 h-8 rounded-md flex items-center justify-center shrink-0" style={{ backgroundColor: `${a.accent}1f`, border: `1px solid ${a.accent}55` }}>
                  <BellRingingIcon weight="fill" size={16} style={{ color: a.accent }} />
                </span>
                <div className="min-w-0">
                  <p className="text-[10px] font-black uppercase tracking-[0.18em] mb-0.5" style={{ color: a.accent }}>{a.title}</p>
                  <p className="text-xs text-white/70 leading-relaxed line-clamp-2">{a.body}</p>
                </div>
              </Link>
            ))}
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2">
          <SectionTitle title="Today's Games" link="/games" linkLabel="See all" testId="section-games" />
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {games.map((g) => (
              <GameCard key={g.id} game={g} />
            ))}
            {games.length === 0 && <Empty text="No games yet" />}
          </div>
        </div>
        <aside>
          <SectionTitle title="Your Storylines" link="/storylines" linkLabel="Generate" testId="section-storylines" />
          <div className="space-y-3">
            {storylines.map((s) => (
              <div key={s.id} className="p-4 rounded-md bg-[#121212] border border-white/10" data-testid={`story-${s.id}`}>
                <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#32D74B] mb-2">{s.category}</p>
                <h3 className="font-display font-bold text-lg uppercase mb-2 tracking-tight">{s.topic}</h3>
                <pre className="whitespace-pre-wrap text-xs text-white/70 font-sans leading-relaxed line-clamp-6">{s.text}</pre>
              </div>
            ))}
            {storylines.length === 0 && (
              <div className="p-4 rounded-md bg-[#121212] border border-white/10">
                <p className="text-sm text-white/60 mb-3">No storylines yet — head to Storylines to generate your first one.</p>
                <Link to="/storylines" data-testid="empty-storylines-cta" className="text-xs font-bold uppercase tracking-wider text-[#0A84FF]">
                  Generate now <ArrowRightIcon size={12} className="inline" />
                </Link>
              </div>
            )}
          </div>
        </aside>
      </div>
    </AppLayout>
  );
}

function KPI({ icon: Icon, label, value, testId }) {
  return (
    <div data-testid={testId} className="p-4 rounded-md bg-[#121212] border border-white/10">
      <div className="flex items-center justify-between mb-2">
        <span className="text-[10px] font-bold uppercase tracking-[0.18em] text-white/40">{label}</span>
        <Icon weight="bold" size={16} className="text-[#0A84FF]" />
      </div>
      <div className="font-num text-4xl font-black tracking-tighter">{value}</div>
    </div>
  );
}

function SectionTitle({ title, link, linkLabel, testId }) {
  return (
    <div className="flex items-end justify-between mb-3" data-testid={testId}>
      <h2 className="font-display font-bold text-2xl md:text-3xl uppercase tracking-tighter">{title}</h2>
      {link && (
        <Link to={link} className="text-[11px] font-bold uppercase tracking-wider text-[#0A84FF] hover:text-blue-300">
          {linkLabel}
        </Link>
      )}
    </div>
  );
}

function Empty({ text }) {
  return <div className="p-6 rounded-md bg-[#121212] border border-white/10 text-white/50 text-sm">{text}</div>;
}
